import React from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { GraduationCap, Palette, Shirt, Music, Drum, HandHeart, BookOpen, ShieldCheck, CalendarClock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { IMAGES } from "../lib/images";
import { Reveal, useSEO } from "../components/site/Reveal";

const AREAS = [
  { icon: GraduationCap, title: "Yorùbá Teaching", text: "Share the language with teens and adults in our year-round educational programmes." },
  { icon: BookOpen, title: "Education Programs", text: "Assist with education programs, in-school events, family activities and more." },
  { icon: Palette, title: "Arts", text: "Support our visual arts gallery exhibitions and creative workshops." },
  { icon: Shirt, title: "Fashion", text: "Help stage and celebrate Yorùbá dress, textiles, and design." },
  { icon: Music, title: "Dancing", text: "Bring energy to our live performing arts and community celebrations." },
  { icon: Drum, title: "Concert", text: "Assist with organising and performing at our music and cultural concerts." },
];

export default function Volunteer() {
  useSEO(
    "Be a Volunteer",
    "Volunteer with ÀsÀNigHT Worldwide in Yorùbá teaching, arts, fashion, dancing, and concerts. Join our altruistic mission and work with us to keep the culture alive."
  );

  const [form, setForm] = React.useState({ name: "", email: "", area: "Education Programs", phone: "", experience: "", availability: "", over18: false });
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/volunteer", form);
      toast.success(data.message || "Thank you for volunteering!");
      setForm({ name: "", email: "", area: "Education Programs", phone: "", experience: "", availability: "", over18: false });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => document.getElementById("work-with-us")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div>
      {/* HERO */}
      <header className="relative min-h-[58vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={IMAGES.traditionalGroup} alt="Yorùbá cultural volunteers" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-16 w-full">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5">Get Involved</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-6xl text-cream leading-tight text-shadow-hero max-w-3xl"
          >
            Be a Volunteer
          </motion.h1>
        </div>
      </header>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <Reveal>
          <p className="text-2xl md:text-3xl font-serif text-forest leading-relaxed border-l-2 border-gold pl-6 mb-10">
            We are an organization with altruistic activities — and we need your gifts to keep
            Yorùbá culture thriving.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="prose-yoruba text-lg text-ink-muted space-y-5">
            <p>
              ÀsÀNigHT Worldwide offers programming in live performing arts, visual arts gallery
              exhibitions, and year-round educational programs for teens and adults. Engagement
              in the arts is vibrant and growing in our community.
            </p>
            <p>
              Join us, and bring your experience and knowledge on board. Below you'll find the
              areas where volunteers are needed — click <strong>Work With Us</strong> to apply.
            </p>
          </div>
        </Reveal>
      </section>

      {/* AREAS */}
      <section className="bg-sand border-y border-parchment">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <Reveal>
            <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">Where We Need You</p>
            <h2 className="font-serif text-4xl md:text-5xl text-ink mb-14">Volunteer Areas</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {AREAS.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.06}>
                <div
                  data-testid={`volunteer-area-${a.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="h-full border border-parchment rounded-xl p-8 bg-cream hover:-translate-y-1 hover:border-gold transition-all duration-300"
                >
                  <span className="grid place-items-center w-12 h-12 rounded-full bg-forest text-cream mb-6">
                    <a.icon className="w-5 h-5" />
                  </span>
                  <h3 className="font-serif text-2xl text-ink mb-3">{a.title}</h3>
                  <p className="text-ink-muted leading-relaxed">{a.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <button
                onClick={scrollToForm}
                data-testid="work-with-us-button"
                className="h-full w-full text-left rounded-xl p-8 bg-forest text-cream hover:bg-forest-light transition-colors flex flex-col justify-between"
              >
                <HandHeart className="w-8 h-8 text-gold mb-6" />
                <div>
                  <h3 className="font-serif text-2xl mb-2">Work With Us</h3>
                  <p className="text-cream/70 text-sm">Ready to give your time and talent? Apply now.</p>
                </div>
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WORK WITH US — EDUCATION VOLUNTEERS */}
      <section className="bg-forest text-cream grain-overlay">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <Reveal>
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft mb-4">Work With Us</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-6">
              ÀsÀNigHT Worldwide is looking for Education Volunteers!
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 text-cream/80 text-lg leading-relaxed max-w-3xl">
              <p>
                We are looking for volunteers for day teaching, or those who have evening and
                weekend availability, to complete a minimum of one shift per month.
              </p>
              <p>
                Education volunteers will assist with education programs, in-school events, family
                activities and more. We are looking for volunteers with daytime and weekend
                availability to complete a minimum of one shift per month.
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-5 mt-12">
            <Reveal delay={0.15}>
              <div className="border border-cream/15 rounded-xl p-7 bg-cream/5 h-full">
                <CalendarClock className="w-6 h-6 text-gold mb-4" />
                <h3 className="font-serif text-2xl mb-2">Minimum Commitment</h3>
                <p className="text-cream/70">At least one shift per month, with daytime, evening, and weekend options available.</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="border border-cream/15 rounded-xl p-7 bg-cream/5 h-full">
                <ShieldCheck className="w-6 h-6 text-gold mb-4" />
                <h3 className="font-serif text-2xl mb-2">Vulnerable Sector Screening</h3>
                <p className="text-cream/70">Volunteers 18 years of age or older will be required to submit a vulnerable sector screening.</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.25}>
            <p className="text-cream/70 mt-10 max-w-3xl leading-relaxed">
              Interested volunteers must submit an application online. All potential volunteers
              will be contacted as to the status of their application. Due to demand we cannot
              guarantee a position for everyone — however, we will do our best to find a position
              that fits you.
            </p>
            <button
              onClick={scrollToForm}
              data-testid="education-volunteer-opportunity-button"
              className="mt-8 inline-flex items-center gap-2 bg-gold text-night font-semibold px-8 py-4 rounded-full hover:bg-gold-light transition-colors"
            >
              <BookOpen className="w-4 h-4" /> Education Volunteer Opportunity
            </button>
          </Reveal>
        </div>
      </section>

      {/* APPLY FORM */}
      <section id="work-with-us" className="scroll-mt-24 max-w-2xl mx-auto px-6 py-24">
        <Reveal>
          <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4 text-center">Work With Us</p>
          <h2 className="font-serif text-4xl md:text-5xl text-ink mb-3 text-center">Volunteer Application</h2>
          <p className="text-ink-muted text-center mb-10">
            Tell us a little about yourself and how you'd like to contribute.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="bg-cream border border-parchment rounded-2xl p-8 space-y-5 shadow-xl shadow-ink/5">
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Full Name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="volunteer-name-input" className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Email</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="volunteer-email-input" className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1.5">Phone (optional)</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="volunteer-phone-input" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Area of Interest</label>
              <select value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="volunteer-area-select" className={inputCls + " bg-white"}>
                {AREAS.map((a) => (
                  <option key={a.title} value={a.title}>{a.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Your experience & knowledge</label>
              <textarea rows={4} value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} data-testid="volunteer-experience-input" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Availability (day / evening / weekend)</label>
              <input value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} data-testid="volunteer-availability-input" placeholder="e.g. Weekends and Tuesday evenings" className={inputCls} />
            </div>
            <label className="flex items-start gap-3 text-sm text-ink-muted bg-sand border border-parchment rounded-md p-4">
              <input type="checkbox" checked={form.over18} onChange={(e) => setForm({ ...form, over18: e.target.checked })} data-testid="volunteer-over18-checkbox" className="w-4 h-4 mt-0.5 accent-forest shrink-0" />
              <span>I am 18 years of age or older and understand I may be required to submit a vulnerable sector screening.</span>
            </label>
            <button type="submit" disabled={loading} data-testid="volunteer-submit-button" className="w-full bg-gold text-night font-semibold py-4 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">
              {loading ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}

const inputCls =
  "w-full border border-parchment rounded-md px-4 py-3 outline-none focus:border-gold focus:ring-1 focus:ring-gold bg-white text-ink";
